import { useEffect, useRef, useState } from "react";
import { Card, Modal, Spin, message } from "antd";

// Fetches a PDF and returns an object URL that is guaranteed to be served as
// application/pdf. The original blob type comes from the storage response and
// must not be trusted (e.g. text/html would execute in our origin). The magic
// bytes are checked too so non-PDF content is never handed to the viewer.
export const fetchPdfObjectUrl = async (url) => {
  const res = await fetch(url, { mode: "cors" });
  if (!res.ok) throw new Error("Failed to fetch PDF");
  const blob = await res.blob();
  const header = await blob.slice(0, 5).text();
  if (header !== "%PDF-") throw new Error("File is not a valid PDF");
  return URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
};

// Rendered with <object type="application/pdf"> instead of an iframe: Chrome's
// PDF viewer does not render inside a sandboxed iframe, and the object element
// with a PDF-typed blob never treats the content as HTML.
export const PdfViewer = ({ src, title, style }) => (
  <object
    data={src}
    type="application/pdf"
    aria-label={title || "pdf-preview"}
    style={{ width: "100%", height: "100%", border: "none", ...style }}
  >
    <a href={src} download={title || "document.pdf"}>
      Download PDF
    </a>
  </object>
);

function PdfMessage({ url, name }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const previewUrlRef = useRef(null);
  const requestIdRef = useRef(0);

  const revokePreview = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
  };

  useEffect(
    () => () => {
      requestIdRef.current += 1;
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    []
  );

  const handleOpen = async () => {
    if (!url || loading) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const objectUrl = await fetchPdfObjectUrl(url);
      if (requestId !== requestIdRef.current) {
        URL.revokeObjectURL(objectUrl);
        return;
      }
      revokePreview();
      previewUrlRef.current = objectUrl;
      setPreviewUrl(objectUrl);
      setOpen(true);
    } catch (err) {
      console.error("Failed to load PDF:", err);
      message.error("Failed to load PDF");
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    revokePreview();
  };

  return (
    <>
      <Card
        size="small"
        style={{
          maxWidth: 220,
          borderRadius: 8,
          cursor: "pointer",
        }}
        onClick={handleOpen}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {loading ? (
            <Spin size="small" />
          ) : (
            <span role="img" style={{ fontSize: 20 }} aria-label="pdf">
              📄
            </span>
          )}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "160px",
              display: "inline-block",
            }}
          >
            {name}
          </span>
        </div>
      </Card>

      <Modal
        title={name}
        open={open}
        onCancel={handleClose}
        footer={null}
        width="85%"
        bodyStyle={{ height: "80vh", padding: 15 }}
        destroyOnClose
      >
        {previewUrl && <PdfViewer src={previewUrl} title={name} />}
      </Modal>
    </>
  );
}

export default PdfMessage;
